import React, {useMemo, useEffect} from 'react';
import {FIELD_TYPES, Field, getVisibility} from 'shared/components/Forms/Field';
import {useFormikContext} from 'formik';
import {Form, Row, Col} from 'react-bootstrap';
import {ColorSwatchSelected} from 'components/customer/Room/ColorSwatches';
import {MaterialSimplified} from 'components/customer/Product/Specs/MaterialSimplified';
import {useProductContext} from 'contexts/ProductContext';
import {Loader} from 'shared/helpers';
import Excel from 'shared/Excel';
import {ColorSwatchContainer} from 'components/customer/Room/ColourSearch';
import {isDeviceSmall, useTabletSize} from 'shared/helpers/DeviceSize';

export const MaterialDefaults = ({fields, hasPreview}) => {
    const {values} = useFormikContext();
    const {
        materialLoading,
        setMaterialFace,
        productDataStore,
        getMaterialOptions,
        isMiniBrowser,
    } = useProductContext();

    const isSmallDevice = isDeviceSmall();
    const isTabletSize = useTabletSize();

    useEffect(() => {
        setMaterialFace(productDataStore.current.include_drawer_faces == 1);
    }, []);

    const [exteriorColorThumbnail, exteriorEdgeColorThumbnail] = useMemo(() => {
        return [
            <ColorSwatchContainer
                $isMobile={isSmallDevice || isTabletSize}
                key={0}>
                <ColorSwatchSelected
                    isExterior={true}
                    horizontal={values.hor_grain_ext}
                />
            </ColorSwatchContainer>,
            <ColorSwatchSelected
                isExterior={true}
                horizontal={values.hor_grain_ext}
                key={1}
                isEdge={true}
            />,
        ];
    }, [values.hor_grain_ext, hasPreview]);

    const [carcaseColorThumbnail, carcaseEdgeColorThumbnail] = useMemo(() => {
        return [
            <ColorSwatchContainer
                $isMobile={isSmallDevice || isTabletSize}
                key={0}>
                <ColorSwatchSelected
                    isExterior={false}
                    horizontal={values.hor_grain_carc}
                />
            </ColorSwatchContainer>,
            <ColorSwatchSelected
                isExterior={false}
                horizontal={values.hor_grain_carc}
                key={1}
                isEdge={true}
            />,
        ];
    }, [values.hor_grain_carc, hasPreview]);

    return (
        <>
            <Loader loader={materialLoading} relative={isMiniBrowser} />
            {fields.map((field, index) => {
                const isExterior = field.name.indexOf('ext') > -1;
                let header = '';

                // Smash a header in before the first line and before the carcase material set
                if (
                    index == 0 ||
                    (field.type == FIELD_TYPES.MATERIAL && !isExterior)
                ) {
                    header = (
                        <Row>
                            <Col>
                                <h3
                                    className="notes-and-variations"
                                    style={{margin: 0}}>
                                    {isExterior ? 'Exterior' : 'Carcase'}
                                </h3>
                            </Col>
                        </Row>
                    );
                }

                if (
                    field.type != FIELD_TYPES.MATERIAL &&
                    field.type != 'edge_material' &&
                    (!field?.options.hasOwnProperty('visible') ||
                        (field?.options.hasOwnProperty('visible') &&
                            Excel.calculate(field.options.visible, values)))
                ) {
                    return (
                        <React.Fragment key={field.name}>
                            {header}
                            <section
                                style={{
                                    marginTop: '15px',
                                }}>
                                <Form.Group
                                    as={Row}
                                    style={{
                                        marginBottom:
                                            field.type != FIELD_TYPES.RADIO
                                                ? '10px'
                                                : '0px',
                                    }}>
                                    <Col
                                        md={
                                            isTabletSize || isMiniBrowser
                                                ? 12
                                                : {offset: 2, span: 10}
                                        }>
                                        <Field
                                            field={field}
                                            type={field.type}
                                            fieldSpan={12}
                                            formControlSpan={
                                                isSmallDevice || isTabletSize
                                                    ? 6
                                                    : 4
                                            }
                                        />
                                    </Col>
                                </Form.Group>
                            </section>
                        </React.Fragment>
                    );
                }

                const isVisible = getVisibility(
                    field,
                    null, // Assuming this is never used
                    values,
                    {}, // Assuming there's never an index
                    getMaterialOptions()
                );

                const isEdgeMaterial = field.name.indexOf('edge') > -1;

                let thumbnail;

                if (isExterior) {
                    if (isEdgeMaterial) {
                        thumbnail = exteriorEdgeColorThumbnail;
                    } else {
                        thumbnail = exteriorColorThumbnail;
                    }
                } else {
                    if (isEdgeMaterial) {
                        thumbnail = carcaseEdgeColorThumbnail;
                    } else {
                        thumbnail = carcaseColorThumbnail;
                    }
                }

                if (isEdgeMaterial) return <React.Fragment key={field.name} />;

                return (
                    <React.Fragment key={field.name}>
                        {header}
                        <MaterialSimplified
                            isVisible={isVisible}
                            isExterior={isExterior}
                            thumb={thumbnail}
                            field={field}
                        />
                    </React.Fragment>
                );
            })}
        </>
    );
};
